// src/components/proposals/ProposalSettingsPanel.jsx
//
// The left-hand settings pane, shared by the Proposal builder and the Template
// editor. Both edit the same content shape; a Template just owns the subset
// that isn't customer-specific, which is what `hideCustomerFields` controls.
import React from "react";
import { PAGE_KEYS, PLAN_OPTIONS, MAX_LIST_ITEMS, computeBronzeTotals, readCoverImage } from "./BronzeTemplate";
import FoundationalEditor from "./FoundationalEditor";
import ClientTypeahead from "../common/ClientTypeahead";

/* Whether this document carries a real Autotask link.
 *
 * ⛔ NEVER A GATE. Most proposals are prospects and always will be — an unlinked
 * proposal is the normal case, not an error, and must not be styled as one. The
 * note exists so that a pre-filled onboarding picker (when linked) and a blank
 * one (when not) are both explicable, rather than the difference looking random.
 *
 * Exported because the Lead panel shows the identical affordance, and the two
 * drifting apart would mean the same field explained two different ways.
 */
export function LinkNote({ linked, name }) {
  if (!name) return null;
  return (
    <div style={{ fontSize: 10, marginTop: 3, lineHeight: 1.35,
                  color: linked ? "var(--dt-secondary)" : "var(--text3)" }}>
      {linked
        ? "✓ Linked to Autotask — onboarding will pre-fill this company"
        : "Not linked to Autotask — fine for a prospect"}
    </div>
  );
}

/* THE section card of the Proposals builder. Its fill is the shared --form-card
   token (MspOsApp.jsx :root) — the same one the Quoting builder's section cards
   use — so the two pages match and the shade is tuned in one place. Inputs,
   buttons and nested cards inside keep their own (white / .inp) backgrounds. */
export function Panel({ title, children }) {
  return (
    <div style={{ background: "var(--form-card)", border: "1px solid var(--border2)", borderRadius: 10,
                  padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                    color: "var(--text3)", marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}



/* Page-visibility switch — teal when on, grey when off, matching the standalone
   template's settings panel. Purely presentational; the row owns the click. */
function ToggleSwitch({ on, disabled }) {
  return (
    <div style={{ width: 36, height: 20, borderRadius: 10, position: "relative", flexShrink: 0,
                  background: on ? "#0b80a5" : "#374151", transition: "background .2s",
                  opacity: disabled ? 0.5 : 1 }}>
      <div style={{ position: "absolute", top: 3, left: on ? 19 : 3, width: 14, height: 14,
                    borderRadius: "50%", background: "#fff", transition: "left .2s" }} />
    </div>
  );
}

export function SettingsPanel({ p, onContent, coverImageUrl, onCover, disabled,
                                hideCustomerFields = false, markupPct = 0,
                                onCatalogSearch,
                                openOpportunities = [], onLinkOpportunity,
                                linkedOpportunity = null,
                                atCompanies = [], onCompany }) {
  const c = p.content || {};
  const t = computeBronzeTotals(c);
  const set = (patch) => { if (!disabled) onContent(patch); };

  /* The picker offers OPEN opportunities, plus the linked one when it has since
     been closed. Without that last entry the select would have a value matching
     no option, render as if nothing were linked, and the next change would
     silently unlink a deal instead of moving it. */
  const opportunityOptions = linkedOpportunity
    && !openOpportunities.some((o) => o.id === linkedOpportunity.id)
    ? [...openOpportunities, { ...linkedOpportunity, closed: true }]
    : openOpportunities;

  const numField = (key, label, step = 1, min = 0) => (
    <div style={{ flex: 1 }}>
      <label className="prop-lbl">{label}</label>
      <input className="inp" type="number" min={min} step={step} disabled={disabled}
             style={{ marginBottom: 0, fontSize: 12 }}
             value={c[key] ?? ""}
             onChange={(e) => set({ [key]: parseFloat(e.target.value) || 0 })} />
    </div>
  );

  return (
    <>
      <style>{`
        .prop-lbl{display:block;font-size:9px;font-weight:700;letter-spacing:.08em;
                  text-transform:uppercase;color:var(--text3);margin-bottom:3px;}
        .prop-row{display:flex;gap:8px;margin-bottom:8px;}
      `}</style>

      {!hideCustomerFields && (
      <Panel title="Customer">
        {/* customerName is what renders as the organisation name throughout the
            document — cover, page headers, body copy. It used to be reachable
            only as a side effect of picking an Autotask company, which made it
            effectively unsettable for prospects, who are most proposals.

            ⚠ IT IS STILL FREE TEXT, and still the display truth. What changed is
            that picking a real Autotask match now ALSO stores an optional
            companyId on the record. Pure free text was the previous fix, and it
            is why zero of twelve real proposals carry a companyId — so the
            onboarding pipeline can never resolve a company on its own and has to
            be told one on every run. The link is what pre-fills that picker.

            ⛔ The proposal does NOT gain a top-level companyName. This field
            remains the only name; a second one would be two sources of truth for
            the same fact. See companyLink.js. */}
        <div style={{ marginBottom: 8 }}>
          <label className="prop-lbl">Company name</label>
          <ClientTypeahead
            value={c.customerName || ""}
            companies={atCompanies}
            placeholder="Company name — type a new one if they're not in Autotask"
            inputStyle={{ marginBottom: 0, fontSize: 12 }}
            onChange={(name, match) => {
              if (disabled) return;
              // ⚠ LINKED ONLY ON AN EXPLICIT PICK, and CLEARED the moment the
              // name is typed over. A stale id names one business while filing
              // against another — and Stage A writes a CONTRACT and financial
              // service lines against it.
              if (onCompany) onCompany(name, match);
              else set({ customerName: name });   // template editor: no record to link
            }} />
          <LinkNote linked={!!p.companyId} name={c.customerName} />
        </div>
        <div className="prop-row">
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Contact full name</label>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   value={c.customerFullName || ""}
                   onChange={(e) => set({ customerFullName: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">First name (salutation)</label>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   value={c.customerContact || ""}
                   onChange={(e) => set({ customerContact: e.target.value })} />
          </div>
        </div>
        {/* Never printed on the proposal — it exists so a contract can actually
            be sent. DocuSign cannot address an envelope without a recipient
            email, and the proposal content model captured no email anywhere. */}
        <div className="prop-row">
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Contact email</label>
            <input className="inp" type="email" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   placeholder="name@client.com.au"
                   value={c.customerEmail || ""}
                   onChange={(e) => set({ customerEmail: e.target.value })} />
            <div style={{ fontSize: 9, color: "var(--text3,#999)", marginTop: 2 }}>
              Not shown on the proposal — used to address the DocuSign contract.
            </div>
          </div>
        </div>
        <div className="prop-row">
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Proposal date</label>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   placeholder="July 2026" value={c.proposalDate || ""}
                   onChange={(e) => set({ proposalDate: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Recommended plan</label>
            <select className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                    value={c.recommendedPlan || "Bronze Sentinel"}
                    onChange={(e) => set({ recommendedPlan: e.target.value })}>
              {PLAN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div className="prop-row" style={{ marginBottom: 0 }}>
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Sales rep</label>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   value={c.salesRepName || ""}
                   onChange={(e) => set({ salesRepName: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="prop-lbl">Title</label>
            <input className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                   value={c.salesRepTitle || ""}
                   onChange={(e) => set({ salesRepTitle: e.target.value })} />
          </div>
        </div>
      </Panel>

      )}

      {/* Links this proposal to an MSP OS Opportunity record. Deliberately NOT
          the Autotask company picker that was removed — this is a pipeline
          link, and it is what turns the opportunity's card orange (or green
          once this proposal is won).

          opportunityId lives on the PROPOSAL, not inside content, so it goes
          through onLinkOpportunity rather than set(). A template has no
          opportunity, hence the same gate as the customer fields. */}
      {!hideCustomerFields && onLinkOpportunity && (
      <Panel title="Opportunity">
        <select className="inp" style={{ marginBottom: 0, fontSize: 12 }} disabled={disabled}
                value={p.opportunityId || ""}
                onChange={(e) => onLinkOpportunity(e.target.value || null)}>
          <option value="">— None —</option>
          {opportunityOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.title || "(untitled)"}{o.companyName ? ` — ${o.companyName}` : ""}
              {o.closed ? " (closed)" : ""}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 9, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
          {p.opportunityId
            ? "Linked. This proposal counts toward that opportunity's pipeline colour."
            : "Optional. Linking moves the opportunity's card from red to orange."}
        </div>
      </Panel>
      )}

      <Panel title="Pricing">
        {!hideCustomerFields && (
          <div className="prop-row">
            {numField("deviceCount", "Devices")}
            {numField("userCount", "Users")}
          </div>
        )}
        <div className="prop-row">
          {numField("perDeviceRate", "$/device/mth", 0.01)}
          {numField("perUserRate", "$/user/mth", 0.01)}
        </div>
        <div className="prop-row">
          {numField("onboardingFee", "Onboarding fee $")}
          {numField("intunePerDevRate", "Intune $/device")}
        </div>
        <div className="prop-row" style={{ marginBottom: 8 }}>
          {numField("intuneSetupFee", "Intune setup $")}
          <div style={{ flex: 1 }} />
        </div>

        {/* Generic mechanism — nothing tier-specific here. Only the seeded
            DEFAULT differs per template (Gold ships with it on). Toggling
            never touches the two amounts above, so a custom fee survives a
            waive/unwaive round trip. */}
        <div onClick={() => { if (!disabled) set({ waiveSetupFees: !c.waiveSetupFees }); }}
             style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "7px 10px", marginBottom: 8, borderRadius: 6,
                      background: "var(--s1)", border: "1px solid var(--border)",
                      cursor: disabled ? "default" : "pointer" }}>
          <span style={{ fontSize: 11, color: "var(--text2)" }}>
            Waive onboarding &amp; Intune setup fees
          </span>
          <ToggleSwitch on={!!c.waiveSetupFees} disabled={disabled} />
        </div>
        {!hideCustomerFields && (
        <div style={{ padding: "8px 10px", background: "var(--s1)", borderRadius: 6,
                      border: "1px solid var(--border)", fontSize: 11 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ color: "var(--text3)" }}>Monthly total (ex GST)</span>
            <strong style={{ color: "var(--text)" }}>
              ${t.monthlyTotal.toLocaleString("en-AU")}
            </strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "var(--text3)" }}>Once-off total (ex GST)</span>
            <strong style={{ color: "var(--text)" }}>
              ${t.onceOffTotal.toLocaleString("en-AU")}
            </strong>
          </div>
        </div>
        )}
      </Panel>

      <Panel title="Introduction letter">
        {["introPara1", "introPara2", "introPara3"].map((k, i) => (
          <div key={k} style={{ marginBottom: i === 2 ? 0 : 8 }}>
            <label className="prop-lbl">Paragraph {i + 1}</label>
            <textarea className="inp" rows={3} disabled={disabled}
                      style={{ marginBottom: 0, fontSize: 12, resize: "vertical" }}
                      value={c[k] || ""} onChange={(e) => set({ [k]: e.target.value })} />
          </div>
        ))}
      </Panel>

      <ListEditor title="What We Heard" itemLabel="Challenge" listKey="challenges"
                  c={c} set={set} disabled={disabled} />
      <ListEditor title="What We've Found" itemLabel="Finding" listKey="findings"
                  c={c} set={set} disabled={disabled} />
      <Panel title="Foundational Upgrade">
        {/* Prints under the section intro on the first Foundational page only.
            Editable per-tier like every other piece of template copy; blank it
            and the line disappears from the document entirely. */}
        <label style={{ display: "block", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                        textTransform: "uppercase", color: "var(--text3)", marginBottom: 2 }}>
          Estimate-only pricing note
        </label>
        <textarea className="inp" rows={3} disabled={disabled}
                  style={{ marginBottom: 4, fontSize: 11, resize: "vertical" }}
                  placeholder="Leave blank to print no pricing caveat"
                  value={c.foundationalPricingNote || ""}
                  onChange={(e) => set({ foundationalPricingNote: e.target.value })} />
        <div style={{ fontSize: 9, color: "var(--text3)", marginBottom: 8 }}>
          Appears once, under the section intro — not on continuation pages.
        </div>

        <FoundationalEditor c={c} set={set} disabled={disabled}
                            markupPct={markupPct} onCatalogSearch={onCatalogSearch} />
      </Panel>

      {!hideCustomerFields && (
      <Panel title="Cover image">
        {coverImageUrl ? (
          <>
            <img src={coverImageUrl} alt="" style={{ width: "100%", height: 80, objectFit: "cover",
                                                     borderRadius: 6, display: "block", marginBottom: 8 }} />
            <button className="btn" style={{ fontSize: 10 }} disabled={disabled}
                    onClick={() => onCover(null)}>Remove</button>
          </>
        ) : (
          <label className="btn" style={{ fontSize: 10, display: "inline-block", cursor: "pointer" }}>
            Upload cover photo
            <input type="file" accept="image/*" style={{ display: "none" }} disabled={disabled}
                   onChange={(e) => readCoverImage(e, (dataUrl) => onCover(dataUrl))} />
          </label>
        )}
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>
          JPG or PNG — auto-compressed to 1600px before saving.
        </div>
      </Panel>
      )}

      <Panel title="Page visibility">
        {PAGE_KEYS.map(([key, label]) => (
          <div key={key}
               onClick={() => { if (!disabled) set({ [key]: c[key] === false }); }}
               style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "6px 8px", marginBottom: 4, borderRadius: 6,
                        background: "var(--s1)", border: "1px solid var(--border)",
                        cursor: disabled ? "default" : "pointer" }}>
            <span style={{ fontSize: 11, color: "var(--text2)" }}>{label}</span>
            <ToggleSwitch on={c[key] !== false} disabled={disabled}
                          onToggle={() => set({ [key]: c[key] === false })} />
          </div>
        ))}
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>
          The cover page is always included.
        </div>
      </Panel>
    </>
  );
}


function ListEditor({ title, itemLabel, listKey, c, set, disabled }) {
  const items = Array.isArray(c[listKey]) ? c[listKey] : [];
  const patch = (i, p) => set({ [listKey]: items.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const remove = (i) => set({ [listKey]: items.filter((_, j) => j !== i) });
  const add = () => {
    if (items.length >= MAX_LIST_ITEMS) return;
    set({ [listKey]: [...items, { title: "", description: "" }] });
  };

  return (
    <Panel title={title}>
      {items.map((item, i) => (
        <div key={i} style={{ background: "var(--s1)", border: "1px solid var(--border)",
                              borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        marginBottom: 5 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--strat)",
                           letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {itemLabel} {i + 1}
            </span>
            <button className="btn" style={{ fontSize: 9, padding: "2px 7px" }} disabled={disabled}
                    onClick={() => remove(i)}>✕</button>
          </div>
          <input className="inp" style={{ marginBottom: 5, fontSize: 12 }} disabled={disabled}
                 placeholder={`${itemLabel} title…`} value={item.title || ""}
                 onChange={(e) => patch(i, { title: e.target.value })} />
          <textarea className="inp" rows={2} disabled={disabled}
                    style={{ marginBottom: 0, fontSize: 12, resize: "vertical" }}
                    placeholder="Describe…" value={item.description || ""}
                    onChange={(e) => patch(i, { description: e.target.value })} />
        </div>
      ))}
      <button className="btn" style={{ fontSize: 10 }} disabled={disabled || items.length >= MAX_LIST_ITEMS}
              onClick={add}>
        + Add {itemLabel} ({items.length}/{MAX_LIST_ITEMS})
      </button>
    </Panel>
  );
}
